// src/components/admin/CustomCSSEditor.jsx
import React, { useState, useEffect } from 'react';
import '../../styles/customcss.css';

function CustomCSSEditor({ customCSS = '', onUpdate, salonId, onSave, saving, savedMessage, onPreview }) {
  const [css, setCSS] = useState(customCSS);
  const [previewActive, setPreviewActive] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lineCount, setLineCount] = useState(1);

  useEffect(() => {
    setCSS(customCSS);
    setHasChanges(false);
  }, [customCSS]);

  useEffect(() => {
    const lines = css.split('\n').length;
    setLineCount(lines);
  }, [css]);

  const handleCSSChange = (e) => {
    const newCSS = e.target.value;
    setCSS(newCSS);
    setHasChanges(newCSS !== customCSS);
  };

  const applyChanges = () => {
    onUpdate(css);
    setHasChanges(false);
  };

  const resetChanges = () => {
    setCSS(customCSS);
    setHasChanges(false);
  };

  const togglePreview = () => {
    if (!previewActive) {
      // Apply CSS temporarily for preview
      const styleElement = document.createElement('style');
      styleElement.id = 'custom-css-preview';
      styleElement.textContent = css;
      document.head.appendChild(styleElement);
    } else {
      // Remove preview CSS
      const styleElement = document.getElementById('custom-css-preview');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    }
    setPreviewActive(!previewActive);
  };

  const insertCommonCSS = (cssSnippet) => {
    const textarea = document.querySelector('.css-textarea');
    const cursorPosition = textarea.selectionStart;
    const newCSS = css.substring(0, cursorPosition) + cssSnippet + css.substring(cursorPosition);
    setCSS(newCSS);
    setHasChanges(true);
    
    // Focus and set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPosition + cssSnippet.length, cursorPosition + cssSnippet.length);
    }, 0);
  };

  const commonCSSSnippets = {
    'Header Styling': `/* Custom header styling */
.preview-header {
  background: linear-gradient(135deg, #your-color1, #your-color2);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}`,
    'Button Styling': `/* Custom button styling */
.service-item .edit-button {
  background-color: #your-color;
  color: white;
  border-radius: 8px;
}`,
    'Typography': `/* Custom typography */
.preview-salon-name {
  font-size: 24px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}`,
    'Layout': `/* Custom layout adjustments */
.preview-section {
  margin-bottom: 20px;
  padding: 20px;
  border-radius: 12px;
}`
  };

  return (
    <div className="css-editor">
      <div className="editor-page-header">
        <h2>Custom CSS</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
          <button className="page-action-btn page-action-save" type="button" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="cssTextarea">
          Add custom CSS to further customize your salon app
          <span style={{ fontSize: '12px', color: 'var(--medium-gray)', marginLeft: '10px' }}>
            ({lineCount} lines)
          </span>
        </label>
        <textarea
          id="cssTextarea"
          className="css-textarea"
          value={css}
          onChange={handleCSSChange}
          rows={15}
          placeholder={`/* Add your custom CSS here */
.admin-container {
  /* your styles */
}

.service-item h3 {
  /* your styles */
}

/* Use CSS variables for colors */
.custom-element {
  background-color: var(--primary-coral);
  color: var(--white);
}`}
        />
      </div>

      <div className="css-editor-help">
        <h4>Quick Insert</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '15px' }}>
          {Object.entries(commonCSSSnippets).map(([name, snippet]) => (
            <button
              key={name}
              type="button"
              onClick={() => insertCommonCSS(snippet)}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                backgroundColor: 'var(--light-gray)',
                border: '1px solid var(--border-gray)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Familjen Grotesk, sans-serif'
              }}
            >
              {name}
            </button>
          ))}
        </div>
        
        <div className="css-examples">
{`/* Available CSS Variables */
:root {
  --primary-coral: #E64C31;
  --light-coral: #FFBDB1;
  --light-gray: #F6F6F6;
  --white: #FFFFFF;
  --dark-gray: #333333;
  --medium-gray: #666666;
  --border-gray: #ddd;
}`}
        </div>
      </div>
      
      <div className="css-tips">
        <h3>CSS Tips & Guidelines</h3>
        <ul>
          <li>Use CSS variables (var(--primary-coral)) for consistent colors</li>
          <li>Test your changes with the Preview button before applying</li>
          <li>Be specific with your selectors to avoid conflicts</li>
          <li>Use !important sparingly, only when necessary</li>
          <li>Check responsive design on mobile devices</li>
          <li>Backup your CSS before making major changes</li>
        </ul>
      </div>
      
      <div className="form-actions">
        <button 
          className={`preview-button ${previewActive ? 'active' : ''}`}
          onClick={togglePreview}
          type="button"
        >
          {previewActive ? 'Disable Preview' : 'Preview Changes'}
        </button>
        
        {hasChanges && (
          <button 
            className="cancel-button"
            onClick={resetChanges}
            type="button"
          >
            Reset Changes
          </button>
        )}
        
        <button 
          className="apply-button"
          onClick={applyChanges}
          disabled={!hasChanges}
          type="button"
        >
          {hasChanges ? 'Apply CSS Changes' : 'CSS Applied'}
        </button>
      </div>
      
      {previewActive && (
        <div className="preview-notice">
          <p>Preview mode active. The custom CSS is being applied temporarily to see changes.</p>
        </div>
      )}
    </div>
  );
}

export default CustomCSSEditor;